<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Plus, RotateCcw, Search, Pencil } from '@lucide/vue'
import {
  Badge, Button, ConfirmAction, EmptyState, FormField, Input, Modal,
  Pagination, Section, Select, Spinner, Table, Textarea, Toolbar, useToast,
} from '@drama/ui'

import {
  createAssetLibraryItem, deleteAssetLibraryItem, getMediaAssetFacets, listAssetLibraryItems, updateAssetLibraryItem,
} from '../../api/asset-library.js'
import { formatBytes, formatDate } from '../intent-video/format.js'

const { t } = useI18n()
const toast = useToast()

// 哪些游戏可选，跟 AllMediaTab 同一个来源（真实有素材的游戏），不是写死的清单。
const packOptions = ref([])
const packId = ref('')

const ASSET_TYPES = ['image', 'video', 'audio', 'prompt']

const loading = ref(false)
const error = ref('')
const items = ref([])
const total = ref(0)
const page = ref(1)
const pages = ref(1)
const filters = reactive({ type: '', q: '' })
const pageSize = ref(50)

const typeOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.assetLibrary.filters.allTypes') },
  ...ASSET_TYPES.map(type => ({ value: type, label: t(`visualGameAdmin.assetLibrary.type.${type}`) })),
])

const formTypeOptions = computed(() => ASSET_TYPES.map(type => ({ value: type, label: t(`visualGameAdmin.assetLibrary.type.${type}`) })))

const columns = computed(() => [
  { key: 'type', label: t('visualGameAdmin.assetLibrary.table.type'), width: '90px' },
  { key: 'tag', label: t('visualGameAdmin.assetLibrary.table.tag'), width: '140px', mono: true },
  { key: 'name', label: t('visualGameAdmin.assetLibrary.table.name'), width: '180px' },
  { key: 'description', label: t('visualGameAdmin.assetLibrary.table.description'), width: '280px' },
  { key: 'content', label: t('visualGameAdmin.assetLibrary.table.content'), width: '200px' },
  { key: 'updated_at', label: t('visualGameAdmin.assetLibrary.table.updatedAt'), width: '170px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '160px', align: 'right', fixed: 'right' },
])

const summary = computed(() => t('visualGameAdmin.assetLibrary.pager.summary', { total: total.value, page: page.value, pages: pages.value }))

// ------------------------------------------------------------------ 列表

onMounted(async () => {
  try {
    const facets = await getMediaAssetFacets()
    packOptions.value = facets.packs.map(pack => ({ value: pack.pack_id, label: pack.pack_title }))
  } catch {
    // 取不到就留空，下面的列表加载会因为 pack_id 缺失直接报错，不装作有游戏可选。
  }
  if (packOptions.value.length > 0) packId.value = packOptions.value[0].value
  load()
})

async function load() {
  if (packId.value === '') return
  loading.value = true
  error.value = ''
  try {
    const data = await listAssetLibraryItems({
      pack_id: packId.value,
      type: filters.type || undefined,
      q: filters.q.trim() || undefined,
      page: page.value,
      limit: pageSize.value,
    })
    items.value = data.items
    total.value = data.total
    pages.value = data.pages
    page.value = data.page
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  page.value = 1
  load()
}

function resetFilters() {
  filters.type = ''
  filters.q = ''
  applyFilters()
}

function changePageSize(size) {
  pageSize.value = size
  applyFilters()
}

function prevPage() {
  page.value -= 1
  load()
}

function nextPage() {
  page.value += 1
  load()
}

function switchPack(next) {
  packId.value = next
  applyFilters()
}

// ------------------------------------------------------------------ 新建 / 编辑

const formOpen = ref(false)
const saving = ref(false)
/** null = 新建；否则是正在编辑的行。 */
const editingItem = ref(null)
const ASSET_KINDS = ['scene', 'character_identity', 'character_state', 'prop']
const kindOptions = computed(() => ASSET_KINDS.map(kind => ({ value: kind, label: t(`visualGameAdmin.assetLibrary.kind.${kind}`) })))

const form = reactive({
  type: 'image', kind: 'character_identity', subject: '', state_label: '',
  tag: '', name: '', description: '', text_content: '', existing_media_asset_id: '',
})
const pendingFile = ref(null)

const isEditing = computed(() => editingItem.value !== null)
const formValid = computed(() => {
  if (form.name.trim() === '') return false
  if (!isEditing.value && form.tag.trim() === '') return false
  if (form.kind === 'character_state' && form.state_label.trim() === '') return false
  if (form.type === 'prompt') return form.text_content.trim() !== '' || isEditing.value
  return isEditing.value || pendingFile.value !== null || form.existing_media_asset_id.trim() !== ''
})

function openCreate() {
  editingItem.value = null
  form.type = 'image'
  form.kind = 'character_identity'
  form.subject = ''
  form.state_label = ''
  form.tag = ''
  form.name = ''
  form.description = ''
  form.text_content = ''
  form.existing_media_asset_id = ''
  pendingFile.value = null
  formOpen.value = true
}

function openEdit(row) {
  editingItem.value = row
  form.type = row.type
  form.kind = row.kind
  form.subject = row.subject ?? ''
  form.state_label = row.state_label ?? ''
  form.tag = row.tag
  form.name = row.name
  form.description = row.description ?? ''
  form.text_content = row.text_content ?? ''
  form.existing_media_asset_id = ''
  pendingFile.value = null
  formOpen.value = true
}

function onFilePicked(event) {
  pendingFile.value = event.target.files?.[0] ?? null
}

/** OSS 对象大多带 Content-Disposition: attachment，<a target="_blank"> 点了直接下载，弹窗看图才是预览。 */
const imagePreviewOpen = ref(false)
const imagePreviewUrl = ref('')
function openImagePreview(url) {
  imagePreviewUrl.value = url
  imagePreviewOpen.value = true
}

/** FileReader 而不是 btoa(String.fromCharCode(...bytes))：大文件会把参数栈打爆。 */
function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error(`读取文件失败: ${file.name}`))
    reader.onload = () => resolve(String(reader.result).split(',')[1])
    reader.readAsDataURL(file)
  })
}

async function submitForm() {
  saving.value = true
  try {
    const file = pendingFile.value
    const filePayload = file
      ? { data_base64: await readAsBase64(file), file_name: file.name, mime: file.type }
      : {}
    if (isEditing.value) {
      const body = {
        kind: form.kind,
        subject: form.subject.trim() || null,
        state_label: form.kind === 'character_state' ? form.state_label.trim() : null,
        name: form.name.trim(),
        description: form.description.trim() || null,
        ...filePayload,
      }
      if (form.type === 'prompt') body.text_content = form.text_content.trim()
      const updated = await updateAssetLibraryItem(editingItem.value.id, body)
      const index = items.value.findIndex(row => row.id === updated.item.id)
      if (index !== -1) items.value[index] = updated.item
    } else {
      const body = {
        pack_id: packId.value,
        type: form.type,
        kind: form.kind,
        subject: form.subject.trim() || null,
        state_label: form.kind === 'character_state' ? form.state_label.trim() : null,
        tag: form.tag.trim(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        // 已有素材直接指过去；没填才走上传新文件——不强制先下载再重新上传一遍。
        ...(form.existing_media_asset_id.trim() ? { media_asset_id: form.existing_media_asset_id.trim() } : filePayload),
      }
      if (form.type === 'prompt') body.text_content = form.text_content.trim()
      await createAssetLibraryItem(body)
      await load()
    }
    formOpen.value = false
    toast.success(t('visualGameAdmin.assetLibrary.form.saved'))
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}

// ------------------------------------------------------------------ 删除

async function removeItem(row) {
  await deleteAssetLibraryItem(row.id)
  items.value = items.value.filter(item => item.id !== row.id)
  total.value -= 1
}

function onDeleted() {
  toast.success(t('visualGameAdmin.assetLibrary.deleted'))
}

function onDeleteError(requestError) {
  toast.error(requestError.message, { duration: 0 })
}
</script>

<template>
  <div class="alp" data-insp-path="apps/visual-game-admin/src/pages/asset-library/CuratedTab.vue">
    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.assetLibrary.curated.pack') }}</label>
          <Select :model-value="packId" :options="packOptions" size="sm" @update:model-value="switchPack" />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.assetLibrary.filters.search') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="t('visualGameAdmin.assetLibrary.filters.searchPlaceholder')"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.assetLibrary.table.type') }}</label>
          <Select v-model="filters.type" :options="typeOptions" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" /> {{ t('visualGameAdmin.assetLibrary.filters.apply') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
          <Button size="sm" @click="openCreate">
            <Plus :size="14" /> {{ t('visualGameAdmin.assetLibrary.create') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState
          v-if="items.length === 0"
          :title="t('visualGameAdmin.assetLibrary.emptyTitle')"
          :description="t('visualGameAdmin.assetLibrary.emptyDescription')"
        />
        <Table v-else :columns="columns" :rows="items">
          <template #type="{ row }">
            <Badge size="sm">{{ t(`visualGameAdmin.assetLibrary.type.${row.type}`) }}</Badge>
          </template>
          <template #description="{ row }">
            <span class="alp__clamp" :title="row.description || ''">{{ row.description || '-' }}</span>
          </template>
          <template #content="{ row }">
            <button v-if="row.type === 'image'" type="button" class="alp__thumb-btn" @click="openImagePreview(row.public_url)">
              <img :src="row.public_url" class="alp__thumb" :alt="row.name" />
            </button>
            <a v-else-if="row.public_url" :href="row.public_url" target="_blank" rel="noopener">
              {{ formatBytes(row.size) }}
            </a>
            <span v-else class="alp__clamp" :title="row.text_content || ''">{{ row.text_content || '-' }}</span>
          </template>
          <template #updated_at="{ row }">{{ formatDate(row.updated_at ?? row.created_at) }}</template>
          <template #actions="{ row }">
            <div class="alp__actions">
              <Button variant="ghost" size="sm" @click="openEdit(row)">
                <Pencil :size="14" /> {{ t('visualGameAdmin.common.edit') }}
              </Button>
              <ConfirmAction
                :label="t('visualGameAdmin.common.delete')"
                :confirm-text="t('visualGameAdmin.assetLibrary.deleteConfirm', { name: row.name })"
                :cancel-label="t('visualGameAdmin.common.cancel')"
                :ok-label="t('visualGameAdmin.common.delete')"
                :pending-label="t('visualGameAdmin.common.working')"
                danger
                :action="() => removeItem(row)"
                @done="onDeleted"
                @error="onDeleteError"
              />
            </div>
          </template>
        </Table>
      </Section>
      <Pagination
        :has-prev="page > 1"
        :has-next="page < pages"
        :loading="loading"
        :summary="summary"
        :prev-label="t('visualGameAdmin.assetLibrary.pager.prev')"
        :next-label="t('visualGameAdmin.assetLibrary.pager.next')"
        :page-size="pageSize"
        :page-size-options="[20, 50, 100]"
        :page-size-label="t('visualGameAdmin.assetLibrary.pager.pageSize')"
        @prev="prevPage"
        @next="nextPage"
        @update:page-size="changePageSize"
      />
    </template>

    <Modal
      :open="formOpen"
      :title="isEditing ? t('visualGameAdmin.assetLibrary.form.editTitle') : t('visualGameAdmin.assetLibrary.form.createTitle')"
      :close-on-backdrop="!saving"
      width="640px"
      @update:open="formOpen = $event"
    >
      <form class="alp__form" @submit.prevent="submitForm">
        <FormField :label="t('visualGameAdmin.assetLibrary.table.type')" required>
          <Select v-model="form.type" :options="formTypeOptions" :disabled="isEditing" block />
        </FormField>
        <FormField :label="t('visualGameAdmin.assetLibrary.table.tag')" :hint="t('visualGameAdmin.assetLibrary.form.tagHint')" required>
          <Input v-model="form.tag" :disabled="isEditing" :maxlength="96" block required />
        </FormField>
        <FormField :label="t('visualGameAdmin.assetLibrary.table.kind')" :hint="t('visualGameAdmin.assetLibrary.form.kindHint')" required>
          <Select v-model="form.kind" :options="kindOptions" block />
        </FormField>
        <FormField
          v-if="form.kind === 'character_identity' || form.kind === 'character_state'"
          :label="t('visualGameAdmin.assetLibrary.table.subject')"
          :hint="t('visualGameAdmin.assetLibrary.form.subjectHint')"
        >
          <Input v-model="form.subject" :maxlength="64" block />
        </FormField>
        <FormField
          v-if="form.kind === 'character_state'"
          :label="t('visualGameAdmin.assetLibrary.table.stateLabel')"
          :hint="t('visualGameAdmin.assetLibrary.form.stateLabelHint')"
          required
        >
          <Input v-model="form.state_label" :maxlength="120" block required />
        </FormField>
        <FormField :label="t('visualGameAdmin.assetLibrary.table.name')" required>
          <Input v-model="form.name" :maxlength="200" block required />
        </FormField>
        <FormField :label="t('visualGameAdmin.assetLibrary.table.description')">
          <Textarea v-model="form.description" :maxlength="2000" :rows="3" block />
        </FormField>
        <FormField
          v-if="form.type === 'prompt'"
          :label="t('visualGameAdmin.assetLibrary.form.textContent')"
          required
        >
          <Textarea v-model="form.text_content" :maxlength="20000" :rows="8" block required />
        </FormField>
        <template v-else-if="!isEditing">
          <FormField :label="t('visualGameAdmin.assetLibrary.form.file')">
            <input type="file" :accept="`${form.type}/*`" :disabled="form.existing_media_asset_id.trim() !== ''" @change="onFilePicked" />
          </FormField>
          <FormField
            :label="t('visualGameAdmin.assetLibrary.form.existingMediaAssetId')"
            :hint="t('visualGameAdmin.assetLibrary.form.existingMediaAssetIdHint')"
          >
            <Input v-model="form.existing_media_asset_id" :disabled="pendingFile !== null" :maxlength="120" block />
          </FormField>
        </template>
        <FormField v-else :label="t('visualGameAdmin.assetLibrary.form.file')" :hint="t('visualGameAdmin.assetLibrary.form.fileReplaceHint')">
          <input type="file" :accept="`${form.type}/*`" @change="onFilePicked" />
        </FormField>
      </form>
      <template #footer>
        <Button variant="ghost" :disabled="saving" @click="formOpen = false">
          {{ t('visualGameAdmin.common.cancel') }}
        </Button>
        <Button variant="primary" :loading="saving" :disabled="!formValid" @click="submitForm">
          {{ t('visualGameAdmin.common.save') }}
        </Button>
      </template>
    </Modal>

    <Modal :open="imagePreviewOpen" width="min(90vw, 960px)" @update:open="imagePreviewOpen = $event">
      <img :src="imagePreviewUrl" class="alp__preview-image" alt="" />
    </Modal>
  </div>
</template>

<style scoped>
.alp__actions { display: flex; gap: var(--drama-space-xs); justify-content: flex-end; }
.alp__clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.5; }
.alp__thumb { width: 64px; height: 64px; object-fit: cover; border-radius: var(--drama-radius-sm); border: 1px solid var(--drama-color-border); }
.alp__thumb-btn { padding: 0; border: none; background: none; cursor: zoom-in; line-height: 0; }
.alp__form { display: grid; gap: var(--drama-space-md); }
.alp__preview-image { display: block; max-width: 100%; max-height: 80vh; margin: 0 auto; border-radius: var(--drama-radius-sm); }
</style>
